/**
 * Hook para gestionar la capa de vías base en el mapa
 *
 * @module core/spatial/hooks
 * @version 1.0
 * @created 2026-03-01
 */

import { useEffect, useRef, useCallback } from 'react';
import { mapEngine, isMapReadyForSources } from '../../map-engine/MapEngineService';
import {
  loadBaseNetworkLayers,
  removeBaseNetworkLayers,
  setBaseNetworkVisibility,
  areBaseNetworkLayersLoaded,
  BASE_NETWORK_LAYER_PREFIX,
} from '../layers/base-network-layer';

/**
 * Configuración del hook
 */
export interface UseBaseNetworkLayerConfig {
  /** ID del proyecto */
  projectId: string | null;
  /** Visibilidad de las capas */
  visible: boolean;
  /** Si hay datos de vías base disponibles */
  hasData: boolean;
}

/**
 * Hook para gestionar la capa de vías base
 *
 * @remarks
 * - Carga las vías base del proyecto cuando el mapa está listo
 * - Sincroniza visibilidad con el estado del UI
 * - Limpia las capas cuando el componente se desmonta
 *
 * @example
 * ```tsx
 * function MapComponent() {
 *   const [map, setMap] = useState<MaplibreMap | null>(null);
 *   const [baseNetworkVisible, setBaseNetworkVisible] = useState(true);
 *   const [hasBaseNetwork, setHasBaseNetwork] = useState(false);
 *
 *   useBaseNetworkLayer({
 *     map,
 *     projectId: 'abc-123',
 *     visible: baseNetworkVisible,
 *     hasData: hasBaseNetwork,
 *   });
 *
 *   return <MapContainer onLoad={setMap} />;
 * }
 * ```
 */
export function useBaseNetworkLayer({
  projectId,
  visible,
  hasData,
}: UseBaseNetworkLayerConfig): void {
  const layersLoadedRef = useRef(false);
  const visibleRef = useRef(visible);
  const isLoadingRef = useRef(false); // Flag para evitar loop infinito
  const styleListenerRegisteredRef = useRef(false); // Flag para saber si el listener ya está registrado
  const lastStyleDataTimeRef = useRef<number>(0); // Timestamp del último styledata procesado

  // Mantener ref actualizado
  useEffect(() => {
    visibleRef.current = visible;
  }, [visible]);

  // ── Función para cargar las capas ──
  const loadLayers = useCallback(async () => {
    const map = mapEngine.getMap();

    if (!map || !projectId || !hasData || !visibleRef.current) {
      return;
    }

    // Evitar cargar si ya estamos en proceso de carga
    if (isLoadingRef.current) {
      console.log('[useBaseNetworkLayer] ⏭️ Already loading, skipping...');
      return;
    }

    // Belt-and-suspenders: skip if source already present
    // (handles false-alarm styledata events that reach handleStyleData when source is intact)
    if (areBaseNetworkLayersLoaded(map)) {
      console.log('[useBaseNetworkLayer] ⏭️ Source already present, skipping reload');
      layersLoadedRef.current = true;
      setBaseNetworkVisibility(map, visibleRef.current);
      return;
    }

    try {
      isLoadingRef.current = true; // Marcar como cargando
      console.log('[useBaseNetworkLayer] 📍 Loading base network layers...');
      await loadBaseNetworkLayers(map, projectId);
      console.log('[useBaseNetworkLayer] ✅ Layers loaded successfully');
      layersLoadedRef.current = true;
      // Aplicar visibilidad actual
      setBaseNetworkVisibility(map, visibleRef.current);
    } catch (error) {
      console.error('[useBaseNetworkLayer] ❌ Error loading layers:', error);
      layersLoadedRef.current = false;
    } finally {
      isLoadingRef.current = false; // Liberar flag
    }
  }, [projectId, hasData]);

  // ── Cargar capas cuando mapa y proyecto están listos + Registrar listener de styledata ──
  useEffect(() => {
    if (!projectId || !hasData) {
      layersLoadedRef.current = false;
      styleListenerRegisteredRef.current = false;
      return;
    }

    let pollInterval: NodeJS.Timeout | null = null;
    let timeoutId: number | null = null;

    /**
     * Handler para el evento 'styledata'
     * Se dispara cuando se carga un nuevo estilo en el mapa (cambio de mapa base)
     */
    const handleStyleData = (e: any): void => {
      // Solo recargar cuando el evento es de tipo 'style' (carga completa del estilo)
      if (e.dataType !== 'style') return;

      // Ignorar si la capa no está visible — no gastar recursos
      if (!visibleRef.current) return;

      // Throttle: no procesar más de una vez por segundo
      const now = Date.now();
      if (now - lastStyleDataTimeRef.current < 1000) {
        return;
      }
      lastStyleDataTimeRef.current = now;

      // Ignorar si estamos en medio de cargar capas (evitar loop)
      if (isLoadingRef.current) {
        return;
      }

      // Verificar si nuestras capas ya no existen (verdadero cambio de estilo)
      const currentMap = mapEngine.getMap();
      if (!currentMap) return;

      // Use source existence as the check (layer IDs changed from IGN → OSM naming,
      // checking a specific layer like autopista_010 would always return null
      // because ROAD_STYLES now uses motorway/trunk/primary OSM category names).
      const layersExistInMap = areBaseNetworkLayersLoaded(currentMap);
      if (layersExistInMap) {
        return;
      }

      console.log('[useBaseNetworkLayer] 🔄 Map style changed, reloading base network layers...');

      // Cancelar timeout anterior si existe
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Debounce: esperar que el estilo esté completamente cargado
      timeoutId = window.setTimeout(() => {
        layersLoadedRef.current = false; // Marcar como no cargadas
        void loadLayers(); // Re-cargar con la visibilidad guardada
      }, 150);
    };

    const tryLoadLayers = (): void => {
      // No cargar si la capa no está visible
      if (!visibleRef.current) return;

      const map = mapEngine.getMap();

      // Usar isStyleLoaded() en vez de loaded() porque:
      // - loaded() espera a que TODOS los tiles estén cargados
      // - isStyleLoaded() solo necesita que el estilo esté listo para agregar capas
      if (!map || !map.isStyleLoaded()) {
        return; // Esperando a que el estilo del mapa esté listo
      }

      // Si las capas ya están cargadas, no hacer nada
      if (layersLoadedRef.current && areBaseNetworkLayersLoaded(map)) {
        if (pollInterval) {
          clearInterval(pollInterval);
          pollInterval = null;
        }
        return;
      }

      console.log('[useBaseNetworkLayer] Initial load triggered');
      void loadLayers();

      // Registrar listener de styledata UNA VEZ que el mapa está listo
      if (!styleListenerRegisteredRef.current) {
        console.log('[useBaseNetworkLayer] 🎧 Registering styledata listener');
        map.on('styledata', handleStyleData);
        styleListenerRegisteredRef.current = true;
      }

      // Detener polling una vez que se cargaron
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
    };

    // Intentar cargar inmediatamente
    tryLoadLayers();

    // Si no funcionó, iniciar polling (solo si la capa debe estar visible)
    const map = mapEngine.getMap();
    if (visibleRef.current && (!map || !map.isStyleLoaded())) {
      pollInterval = setInterval(tryLoadLayers, 100);
    }

    // Cleanup: remover layers y listener cuando cambia el proyecto
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      const currentMap = mapEngine.getMap();
      if (currentMap) {
        if (styleListenerRegisteredRef.current) {
          currentMap.off('styledata', handleStyleData);
          styleListenerRegisteredRef.current = false;
        }
        if (layersLoadedRef.current) {
          removeBaseNetworkLayers(currentMap);
          layersLoadedRef.current = false;
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, hasData]); // Removido loadLayers para evitar loop infinito


  // ── Sincronizar visibilidad ──
  useEffect(() => {
    const map = mapEngine.getMap();
    console.log('[useBaseNetworkLayer] Visibility effect triggered:', {
      visible,
      layersLoaded: layersLoadedRef.current,
      mapExists: !!map,
      hasData,
      projectId,
    });

    if (!map) {
      console.log('[useBaseNetworkLayer] ⏭️ No map, skipping visibility sync');
      return;
    }

    // Verificar si las capas están realmente en el mapa
    const layersExist = areBaseNetworkLayersLoaded(map);
    const source = isMapReadyForSources(map) ? map.getSource(BASE_NETWORK_LAYER_PREFIX.replace('-layer-', '-source')) : undefined;
    console.log('[useBaseNetworkLayer] Layers exist in map:', layersExist);
    console.log('[useBaseNetworkLayer] Source info:', {
      sourceExists: !!source,
      sourceId: BASE_NETWORK_LAYER_PREFIX.replace('-layer-', '-source'),
    });

    if (!layersExist) {
      console.log('[useBaseNetworkLayer] ⏭️ Layers not loaded yet, skipping visibility sync');
      // Solo cargar si la capa está visible — no gastar recursos en carga invisible
      if (hasData && projectId && visible) {
        console.log('[useBaseNetworkLayer] 🔄 hasData=true, visible=true, no layers → triggering load...');
        void loadLayers();
      }
      return;
    }

    // Aplicar visibilidad
    console.log('[useBaseNetworkLayer] ✅ Applying visibility:', visible);
    setBaseNetworkVisibility(map, visible);
  }, [visible, hasData, projectId, loadLayers]);

  // ── Cleanup al desmontar ──
  useEffect(() => {
    return () => {
      const map = mapEngine.getMap();
      if (map && layersLoadedRef.current) {
        removeBaseNetworkLayers(map);
        layersLoadedRef.current = false;
      }
    };
  }, []);
}
